import React, { useState, useEffect } from "react";
import axios from "axios";
import { Form, Alert, Row, Col } from "react-bootstrap";
import Preloader from "../../../shared/PreLoaders";
import { Authorization } from "../../../Context/Authorization";

const Meetings = () => {
  const [meetingType, setMeetingType] = useState("");
  const [meetingNotes, setMeetingNotes] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingAttendees, setMeetingAttendees] = useState("");
  const [meetingMinutes, setMeetingMinutes] = useState(null);
  const [vfcId, setVfcId] = useState("");
  const [vfcs, setVfcs] = useState([]);
  const [previousMeetings, setPreviousMeetings] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const Api = process.env.REACT_APP_API;

  const handleFileChange = (e) => {
    setMeetingMinutes(e.target.files[0]);
  };

  // Fetch VFCs on component mount
  useEffect(() => {
    setLoading(true);
    const fetchVfc = async () => {
      try {
        const Config = Authorization();
        const response = await axios.get(`${Api}/api/vfcs`, Config);
        setVfcs(response.data.vfcs);
      } catch (error) {
        console.log("Error fetching Data", error);
      }
      setLoading(false);
    };
    fetchVfc();
  }, [Api]);

  // Fetch previous meetings when vfcId changes
  useEffect(() => {
    if (vfcId) {
      handleFetchPreviousMeetings();
    }
  }, [vfcId]);

  const handleFetchPreviousMeetings = async () => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await axios.post(
        `${Api}/api/vfc/previous`,
        { vfcId },
        config
      );
      setPreviousMeetings(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching previous meetings", err);
      setError("Failed to load previous meetings.");
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("vfcId", vfcId);
    formData.append("reportType", "meeting");
    formData.append("meetingType", meetingType);
    formData.append("meetingAttendees", meetingAttendees);
    formData.append("meetingSummary", meetingNotes);
    formData.append("meetingDate", meetingDate);
    if (meetingMinutes) {
      formData.append("meetingMinutes", meetingMinutes);
    }

    try {
      const config = Authorization();
      const response = await fetch(`${Api}/api/vfc/meetings`, {
        method: "POST",
        headers: {
          ...config.headers,
        },
        body: formData,
      });

      const data = await response.json();
      if (response.status === 201) {
        setSuccessMessage("Meeting recorded successfully.");
        setSuccessMessage("");
        setMeetingType("");
        setMeetingNotes("");
        setMeetingDate("");
        setVfcId("");
        setMeetingMinutes(null);
        handleFetchPreviousMeetings();
      } else {
        setError(data.message || "Error submitting form.");
      }
    } catch (error) {
      console.error("Error recording meeting", error);
      setError("Failed to record meeting.");
    }
  };

  return (
    <div>
      <Preloader loading={loading} />
      <div className="page-header">
        <h3 className="page-title">Meeting Reports</h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Meetings Reports
            </li>
          </ol>
        </nav>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}
      <Row>
        <Col md={5}>
          <div className="col-md-12 grid-margin stretch-card">
            <div className="card">
              <div className="card-body">
                <p className="card-description">VFC Meetings</p>
                <form className="forms-sample" onSubmit={handleSubmit}>
                  <Form.Group controlId="formVFC">
                    <label htmlFor="vfcId">VFC Name</label>
                    <Form.Control
                      as="select"
                      value={vfcId}
                      onChange={(e) => setVfcId(e.target.value)}
                      required
                    >
                      <option value="">-- Select VFC --</option>
                      {vfcs.map((vfc) => (
                        <option key={vfc._id} value={vfc._id}>
                          {vfc.vfcName}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <Form.Group controlId="formMeetingType">
                    <Form.Label>Meeting Type</Form.Label>
                    <Form.Control
                      as="select"
                      value={meetingType}
                      onChange={(e) => setMeetingType(e.target.value)}
                      required
                    >
                      <option>Select Meeting Type</option>
                      <option value="EC Meetings">EC Meetings</option>
                      <option value="General Body">General Body</option>
                      <option value="Monthly Meetings">Monthly Meetings</option>
                      <option value="Others">Others</option>
                    </Form.Control>
                  </Form.Group>
                  <Form.Group controlId="formMeetingNotes">
                    <Form.Label>Meeting Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      value={meetingNotes}
                      onChange={(e) => setMeetingNotes(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formMeetingDate">
                    <Form.Label>Meeting Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={meetingDate}
                      onChange={(e) => setMeetingDate(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formMeetingAttendees">
                    <Form.Label>Meeting Attendees</Form.Label>
                    <Form.Control
                      type="Number"
                      value={meetingAttendees}
                      min="1"
                      max=""
                      onChange={(e) => setMeetingAttendees(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formMeetingMinutes">
                    <Form.Label>Upload Minutes</Form.Label>
                    <Form.Control
                      type="file"
                      name="meetingMinutes"
                      onChange={handleFileChange}
                    />
                  </Form.Group>
                  <button
                    type="submit"
                    className="btn btn-outline-primary mt-3"
                  >
                    Submit
                  </button>
                  <button className="btn btn-light mt-3">Cancel</button>
                </form>
              </div>
            </div>
          </div>
        </Col>

        <Col md={7}>
          <p className="breadcrumb-item active" aria-current="page">
            Previous Meetings
          </p>
          <div className="card">
            <div className="card-body">
              <div className="row icons-list">
                <table striped bordered hover className="table-responsive">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>VFC Name</th>
                      <th>Meeting Type</th>
                      <th>Meeting Date</th>
                      <th>Meeting Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previousMeetings.map((meeting, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{meeting.vfcId.vfcName}</td>
                        <td>{meeting.meetingType}</td>
                        <td>
                          {new Date(meeting.meetingDate).toLocaleDateString()}
                        </td>
                        <td>{meeting.meetingSummary}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Meetings;
